import "./ADOfferCard.scss";

import { FC } from "react";
import { useSelector } from "react-redux";

import ADOfferLegalese from "components/ADOfferLegalese/ADOfferLegalese";
import Icon from "components/Icon/Icon";
import OfferCard from "components/OfferCard/OfferCard";
import Text from "components/Text/Text";
import { ButtonFormItem, DynamicFormItems, IProductOffering } from "config";
import { useTranslation } from "modules";
import { RootState, useAppDispatch } from "store";
import { eventFired } from "store/actions/analytics";
import { setModal } from "store/actions/modal";
import { APPLY_AD_PRIMARY_BUTTON_KEY, QUOTE_REVIEW_ACCEPT_PRIMARY_BUTTON_KEY } from "utils/form";
import formatCommas from "utils/formatCommas";

import { DateTime } from "luxon";

interface IProps {
  mode?: "info" | "purchase";
  productOffering: IProductOffering;
  dynamicFormItems?: DynamicFormItems[];
  showPremiumCalculationModal?: boolean;
  onClickEdit(): void;
}

const ADOfferCard: FC<IProps> = props => {
  // HOOKS
  const { t } = useTranslation();
  const {
    productOffering,
    showPremiumCalculationModal = false,
    mode = "info",
    dynamicFormItems,
    onClickEdit,
  } = props;
  const dispatch = useAppDispatch();

  const totalWyshAllocations = useSelector(
    (state: RootState) => state.allocationSummary.totalAmount
  );

  // LOCAL DERIVED VARIABLES
  const premium =
    productOffering.premiums?.find(premium => {
      return premium.billingFrequency === "monthly";
    })?.amount || 0;

  const validUntilDate = DateTime.fromISO(productOffering.expiresAt).toFormat("MM/dd/yyyy");

  const applyAdButton = dynamicFormItems?.find(
    item => item.key === APPLY_AD_PRIMARY_BUTTON_KEY
  ) as ButtonFormItem;

  const acceptAdButton = dynamicFormItems?.find(
    item => item.key === QUOTE_REVIEW_ACCEPT_PRIMARY_BUTTON_KEY
  ) as ButtonFormItem;

  const detailsItems = [
    {
      label: t("offer.card.details.coverage.label", "Total Coverage"),
      value: t("offer.ad.card.details.coverage.value", "Up to\n%<coverage>s", {
        coverage: `$${formatCommas(productOffering.amount)}`,
      }),
    },
    {
      label: t("offer.ad.card.details.age.label", "Guaranteed Coverage"),
      value: t("offer.ad.card.details.age.value", "Until you're\n%<age>s y/o", {
        age: productOffering.insuredsAgeAtTerm,
      }),
    },
  ];

  if (totalWyshAllocations > 0) {
    detailsItems.push({
      label: t("offer.card.details.wyshes.label", "Wyshes"),
      value: t("offer.card.details.wyshes.value", "%<percent>s% covered", {
        percent: productOffering.allocationCoverage,
      }),
    });
  }

  // METHODS
  const onClickLearnMore = () => {
    if (mode === "info") {
      dispatch(
        eventFired({
          event: "fulfillment_quote_ad_learnmore",
        })
      );
    }
  };

  const openPremiumCalculationModal = () => dispatch(setModal("PremiumCalculationModal"));

  // RENDER
  return (
    <div className="offer-card__container">
      <OfferCard
        ctaText={
          mode === "info" ? t("offer.ad.card.cta", "LEARN MORE") : t("ad.about.card.cta", "GET NOW")
        }
        detailsItems={detailsItems}
        onClickCta={onClickLearnMore}
        onClickEdit={onClickEdit}
        premium={premium}
        secondaryCtaText={t("offer.card.cta.edit", "CUSTOMIZE YOUR COVERAGE AND PRICE")}
        theme="light"
        title={t("offer.ad.card.title", "Accidental death policy")}
        validUntilDate={validUntilDate}
        buttonItem={mode === "info" ? applyAdButton : acceptAdButton}
      />
      <ADOfferLegalese payoutDetails />
      {showPremiumCalculationModal && (
        <div className="offer-card__ad-info" onClick={openPremiumCalculationModal}>
          <Icon className="offer-card__ad-info-icon" src="info" size="sm" color="cta" />
          <Text
            className="offer-card__ad-info-text"
            tag="l6"
            text={t(
              "offerings_review.premium_calculation.link",
              "Learn how we calculate monthly premiums"
            )}
          />
        </div>
      )}
    </div>
  );
};

export default ADOfferCard;
